import type { CramCardStats } from '@features/Session';
import { MAX_SESSION_CARDS, sampleIds } from './builderConfig';
import { takeUpTo, type CardWeight } from './cardWeight';

export type CardFilter = 'today' | 'all' | 'mistakes' | 'frequent';

/** History only narrows the current ready-card scope; it cannot add stale,
 * hidden, or other-module states. Correct answers do not erase past mistakes.
 * `today` keeps the server order (repeat first, then new — /sessions/cram/{id}/today). */
export function eligibleCardIds(
  allIds: string[],
  stats: CramCardStats[],
  filter: CardFilter,
  todayIds: string[] = [],
): string[] {
  const uniqueIds = [...new Set(allIds)];
  if (filter === 'all') return uniqueIds;
  if (filter === 'today') {
    const ready = new Set(uniqueIds);
    return [...new Set(todayIds)].filter((id) => ready.has(id));
  }

  const byState = new Map(stats.map((item) => [item.stateId, item]));
  const eligible = uniqueIds.filter((id) => (byState.get(id)?.mistakeCount ?? 0) > 0);
  if (filter === 'mistakes') return eligible;

  // Stable Array.sort keeps the module's original ordering for exact ties.
  return eligible.sort((left, right) => {
    const a = byState.get(left)!;
    const b = byState.get(right)!;
    return b.mistakeCount - a.mistakeCount || a.attemptCount - b.attemptCount;
  });
}

/** Most-mistaken and «for today» selections keep their ranking; other scopes allow variety.
 *  `count` is in cards (see cardWeight). */
export function selectFilteredIds(
  eligibleIds: string[],
  count: number,
  filter: CardFilter,
  weight: CardWeight = () => 1,
): string[] {
  if (filter !== 'frequent' && filter !== 'today') return sampleIds(eligibleIds, count, Math.random, weight);
  const limit = Math.min(MAX_SESSION_CARDS, Number.isNaN(count) ? 0 : Math.max(0, Math.floor(count)));
  return takeUpTo(new Set(eligibleIds), limit, weight);
}
